import {
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Link,
} from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import { ChevronDown } from "lucide-react";
import { AssignmentOpportunity } from "protogen/assignments_pb";
import { useEffect, useState } from "react";

interface AssignmentActionsProps {
  disabled?: boolean;
  onAccept: () => void;
  onDecline: (reason: string | null) => void;
}

const AssignmentActions = ({
  disabled,
  onAccept,
  onDecline,
}: AssignmentActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleDeclineClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeclineClose = (reason?: string) => {
    setAnchorEl(null);
    if (reason) {
      onDecline(reason === "Other" ? null : reason);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Button
        variant="contained"
        onClick={onAccept}
        disabled={disabled}
        sx={{ flex: 1 }}
      >
        Accept
      </Button>
      <Button
        variant="outlined"
        onClick={handleDeclineClick}
        disabled={disabled}
        sx={{ flex: 1 }}
        endIcon={<ChevronDown />}
      >
        Decline
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleDeclineClose()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleDeclineClose("Not a good fit")}>
          Not a good fit
        </MenuItem>
        <MenuItem onClick={() => handleDeclineClose("Not a good time")}>
          Not a good time
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeclineClose("Other")}>Other</MenuItem>
      </Menu>
    </Box>
  );
};

interface ClientOpportunityProps {
  disabled?: boolean;
  assignmentOpportunity: AssignmentOpportunity;
  senderView: boolean;
  onAccept: () => void;
  onDecline: (reason: string | null) => void;
}

export default ({
  disabled,
  assignmentOpportunity,
  senderView,
  onAccept,
  onDecline,
}: ClientOpportunityProps) => {
  const isMobile = useIsMobile();
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpired, setIsExpired] = useState<boolean>(
    assignmentOpportunity.state === "timed-out",
  );

  const location = assignmentOpportunity.location || "";
  const approximateDistanceMiles =
    assignmentOpportunity.approximateDistanceMiles || "";
  const description = assignmentOpportunity.description || "";
  const rate = assignmentOpportunity.monthlyRateCents
    ? `$${assignmentOpportunity.monthlyRateCents / 100}/mo`
    : "";
  let hours = "";
  if (assignmentOpportunity.isPayAsYouGo) {
    hours = "Pay as you go at $50/hr";
  } else if (assignmentOpportunity.monthlyHours) {
    hours = `${assignmentOpportunity.monthlyHours} hours`;
  }
  const expiresAt = assignmentOpportunity.expiresAtSec
    ? new Date(Number(assignmentOpportunity.expiresAtSec) * 1000)
    : null;

  const isDisabled =
    disabled || isExpired || assignmentOpportunity.state !== "active-waiting";

  useEffect(() => {
    if (isDisabled || !expiresAt) return;
    const updateTimer = () => {
      const now = new Date();
      const diff = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);
      setTimeRemaining(diff);
      setIsExpired(diff <= 0);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  const formatTimeRemaining = () => {
    if (isExpired) return "Expired";

    const hours = Math.floor(timeRemaining / 3600);
    const minutes = Math.floor((timeRemaining % 3600) / 60);
    const seconds = timeRemaining % 60;

    let timeStr = "";
    if (hours > 0) {
      timeStr = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      timeStr = `${minutes}m ${seconds}s`;
    } else {
      timeStr = `${seconds}s`;
    }

    return isMobile ? `Expires ${timeStr}` : `Expires in ${timeStr}`;
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        width: "100%",
        maxWidth: "600px",
        opacity: isExpired ? 0.7 : 1,
        minWidth: isMobile ? "90vw" : "500px",
        border: "1px solid #D4D4D4",
        overflow: "hidden",
        padding: isMobile ? "16px 20px" : "20px 24px 24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
        }}
      >
        <Typography variant="h4">Client Opportunity</Typography>
        {assignmentOpportunity.state === "accepted" && (
          <Typography variant="bodyHeavy" color="success.main">
            Accepted
          </Typography>
        )}
        {assignmentOpportunity.state === "declined" && (
          <Typography variant="bodyHeavy" color="text.secondary">
            Declined
          </Typography>
        )}
        {assignmentOpportunity.state === "active-waiting" ||
        assignmentOpportunity.state === "timed-out" ? (
          <Typography
            variant="bodySmall"
            color={isExpired ? "error.main" : "text.secondary"}
            sx={{ fontWeight: isExpired ? "bold" : "500" }}
          >
            {formatTimeRemaining()}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <Typography variant="bodyHeavy">{location}</Typography>
            {approximateDistanceMiles && (
              <Typography variant="bodySmall">
                ({approximateDistanceMiles})
              </Typography>
            )}
          </Box>
          <Typography variant="bodyHeavy">
            {rate}, {hours}
          </Typography>
        </Box>

        <Typography
          variant="body"
          sx={{
            whiteSpace: "pre-line",
          }}
        >
          {description}
        </Typography>
        {senderView && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {assignmentOpportunity.state === "accepted" &&
            assignmentOpportunity.familyRef ? (
              <Link
                href={`/families/${encodeURIComponent(
                  assignmentOpportunity.familyRef,
                )}`}
              >
                View {assignmentOpportunity.familyName} family
              </Link>
            ) : (
              <Link
                href={`/tools/matchmaking/${encodeURIComponent(
                  assignmentOpportunity.signupRef,
                )}`}
              >
                View lead
              </Link>
            )}
          </Box>
        )}
        {!senderView && (
          <>
            {assignmentOpportunity.state === "accepted" &&
              assignmentOpportunity.familyRef && (
                <Link
                  href={`/families/${encodeURIComponent(
                    assignmentOpportunity.familyRef,
                  )}`}
                >
                  View {assignmentOpportunity.familyName} family
                </Link>
              )}
            {assignmentOpportunity.state === "active-waiting" && (
              <AssignmentActions
                disabled={isDisabled}
                onAccept={onAccept}
                onDecline={onDecline}
              />
            )}
            {assignmentOpportunity.state === "declined" && (
              <Box sx={{ display: "flex" }}>
                <Typography variant="bodySmall">
                  Taking a break from new clients? You can always toggle these
                  opportunities off in your{" "}
                  <Link href="/settings">settings</Link>.
                </Typography>
              </Box>
            )}
            {isExpired && (
              <Box sx={{ display: "flex" }}>
                <Typography variant="bodySmall">
                  If you're still interested in this opportunity, reply below to
                  let us know and check if the client is still available.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
